/**
 * Les brouillons de saisie : ce qu'on tape survit à un rechargement.
 *
 * Le patron : « si la page se recharge, ce qui a été saisi doit toujours être
 * là ». La cause la plus fréquente est corrigée dans `authStore` (le retour
 * sur l'onglet démontait l'application), mais il en reste d'autres qu'aucun
 * code ne peut empêcher : le téléphone qui tue l'onglet pendant qu'on prend
 * une photo, le geste qui tire la page vers le bas, une mise en ligne, une
 * batterie à plat. Contre celles-là, un seul remède : écrire la saisie à
 * mesure, et la reproposer.
 *
 * ══════════════════════════════════════════════════════════════
 * Où, et combien de temps
 * ══════════════════════════════════════════════════════════════
 * **Dans `localStorage`**, et non `sessionStorage` : un onglet tué par le
 * système puis rouvert ne retrouve pas toujours sa session de stockage, et
 * c'est précisément le cas qu'on veut couvrir sur un téléphone de chantier.
 *
 * Mais une fiche client est une donnée personnelle, et une tablette de
 * chantier passe de main en main. D'où trois bornes :
 *
 *   · la clé porte l'identifiant de l'utilisateur — le brouillon d'un autre
 *     ne s'affiche jamais ;
 *   · **la déconnexion efface tous les brouillons** de l'appareil ;
 *   · **sept jours**, puis le brouillon est jeté : au-delà, ce n'est plus une
 *     saisie interrompue, c'est un oubli, et le reproposer surprendrait.
 *
 * Fonctions pures sur un `Storage` passé en paramètre : testables sans
 * navigateur, et aucune ne lève — un stockage refusé (navigation privée,
 * quota) coûte le filet, jamais l'écran.
 */

export const DRAFT_PREFIX = "dker:brouillon:";
/**
 * « Cette fenêtre était ouverte » — posé à l'ouverture d'un formulaire, retiré
 * à sa fermeture. Un rechargement ne laisse pas React fermer quoi que ce soit :
 * la marque survit, et la page sait qu'elle doit rouvrir la fenêtre où l'on
 * tapait. Une fenêtre fermée à la main ne se rouvre pas toute seule.
 */
export const DRAFT_OPEN_PREFIX = "dker:ouvert:";
export const DRAFT_TTL_DAYS = 7;
const TTL_MS = DRAFT_TTL_DAYS * 24 * 60 * 60 * 1000;

/** Ce qui est écrit. `v` permet de jeter proprement un format qui changerait. */
export interface StoredDraft<T> {
  v: 1;
  savedAt: string;
  values: T;
}

type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;

export function draftKey(userId: string, name: string): string {
  return `${DRAFT_PREFIX}${userId}:${name}`;
}

export function openMarkerKey(userId: string, name: string): string {
  return `${DRAFT_OPEN_PREFIX}${userId}:${name}`;
}

function parse<T>(brut: string | null): StoredDraft<T> | null {
  if (!brut) return null;
  try {
    const d = JSON.parse(brut) as Partial<StoredDraft<T>>;
    if (d?.v !== 1 || typeof d.savedAt !== "string" || d.values === undefined) return null;
    if (Number.isNaN(new Date(d.savedAt).getTime())) return null;
    return d as StoredDraft<T>;
  } catch {
    return null;
  }
}

function expired(d: StoredDraft<unknown>, now: Date): boolean {
  return now.getTime() - new Date(d.savedAt).getTime() > TTL_MS;
}

/** Le brouillon, s'il existe et qu'il est lisible et récent. Un brouillon périmé ou illisible est effacé au passage. */
export function readDraft<T>(storage: DraftStorage, key: string, now = new Date()): StoredDraft<T> | null {
  try {
    const brut = storage.getItem(key);
    const d = parse<T>(brut);
    if (brut !== null && (!d || expired(d, now))) {
      storage.removeItem(key);
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function writeDraft<T>(storage: DraftStorage, key: string, values: T, now = new Date()): void {
  try {
    const d: StoredDraft<T> = { v: 1, savedAt: now.toISOString(), values };
    storage.setItem(key, JSON.stringify(d));
  } catch {
    // Quota plein ou stockage refusé : la saisie continue, sans filet.
  }
}

export function clearDraft(storage: DraftStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* rien à faire */
  }
}

function draftKeys(storage: DraftStorage, prefix = DRAFT_PREFIX): string[] {
  const cles: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k?.startsWith(prefix)) cles.push(k);
  }
  return cles;
}

export function markOpen(storage: DraftStorage, userId: string, name: string): void {
  try {
    storage.setItem(openMarkerKey(userId, name), "1");
  } catch {
    /* sans marque, la fenêtre ne se rouvrira pas seule — la saisie reste là */
  }
}

export function unmarkOpen(storage: DraftStorage, userId: string, name: string): void {
  try {
    storage.removeItem(openMarkerKey(userId, name));
  } catch {
    /* rien à faire */
  }
}

/**
 * Les formulaires interrompus : ouverts quand la page s'est fermée, **et**
 * porteurs d'une saisie. Une fenêtre ouverte puis abandonnée vide ne se
 * rouvre pas.
 */
export function interruptedDraftNames(storage: DraftStorage, userId: string, now = new Date()): string[] {
  try {
    const debut = `${DRAFT_OPEN_PREFIX}${userId}:`;
    return draftKeys(storage, debut)
      .map((k) => k.slice(debut.length))
      .filter((name) => {
        if (readDraft(storage, draftKey(userId, name), now) !== null) return true;
        // Ouverte vide : la marque n'a plus d'objet.
        storage.removeItem(openMarkerKey(userId, name));
        return false;
      });
  } catch {
    return [];
  }
}

/** À la déconnexion : l'appareil ne garde rien de ce qu'on y a tapé. */
export function clearAllDrafts(storage: DraftStorage): void {
  try {
    [...draftKeys(storage), ...draftKeys(storage, DRAFT_OPEN_PREFIX)].forEach((k) =>
      storage.removeItem(k),
    );
  } catch {
    /* rien à faire */
  }
}

/** Les brouillons oubliés sur d'autres écrans, jetés à l'ouverture de n'importe quel formulaire. */
export function purgeExpiredDrafts(storage: DraftStorage, now = new Date()): void {
  try {
    draftKeys(storage).forEach((k) => readDraft(storage, k, now));
  } catch {
    /* rien à faire */
  }
}

/** Vide au sens de la saisie : rien tapé, ou tout effacé. */
function vide(v: unknown): boolean {
  return (
    v === undefined ||
    v === null ||
    (typeof v === "string" && v.trim() === "") ||
    (typeof v === "number" && Number.isNaN(v))
  );
}

/**
 * La saisie n'a-t-elle rien apporté aux valeurs de départ ?
 *
 * Une comparaison stricte ne suffit pas : un champ que react-hook-form
 * enregistre vaut `""` là où les valeurs de départ ne le mentionnaient pas, et
 * un nombre effacé vaut `NaN`. Sans cette tolérance, ouvrir un formulaire et
 * cliquer dans un champ suffirait à laisser un « brouillon » vide qu'on
 * reproposerait ensuite.
 */
export function sameAsDefaults(values: unknown, defaults: unknown): boolean {
  if (vide(values) && vide(defaults)) return true;
  if (Array.isArray(values) || Array.isArray(defaults)) {
    const a = Array.isArray(values) ? values : [];
    const b = Array.isArray(defaults) ? defaults : [];
    return a.length === b.length && a.every((x, i) => sameAsDefaults(x, b[i]));
  }
  if (
    values !== null &&
    defaults !== null &&
    typeof values === "object" &&
    typeof defaults === "object"
  ) {
    const a = values as Record<string, unknown>;
    const b = defaults as Record<string, unknown>;
    const cles = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...cles].every((k) => sameAsDefaults(a[k], b[k]));
  }
  if (typeof values === "object" && values !== null) return vide(defaults) && sameAsDefaults(values, {});
  if (typeof defaults === "object" && defaults !== null) return vide(values) && sameAsDefaults({}, defaults);
  return values === defaults;
}

const heure = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" });
const jourMois = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long" });

/**
 * « aujourd'hui à 10:42 », « hier à 18:05 », « le 9 septembre à 07:30 ».
 *
 * L'heure dit au patron de quelle saisie il s'agit — celle de ce matin chez le
 * client, ou celle d'avant-hier qu'il croyait abandonnée.
 */
export function draftTimeText(savedAt: Date, now = new Date()): string {
  const jour = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const ecart = Math.round((jour(now) - jour(savedAt)) / 86_400_000);
  const a = `à ${heure.format(savedAt)}`;
  if (ecart === 0) return `aujourd'hui ${a}`;
  if (ecart === 1) return `hier ${a}`;
  return `le ${jourMois.format(savedAt)} ${a}`;
}
